import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';

// Debounce utility for getSession calls
let getSessionTimeout: NodeJS.Timeout | null = null;
let lastSessionCall = 0;
const DEBOUNCE_DELAY = 1000; // 1 second

/**
 * Debounced getSession to prevent noisy polling
 * Use this instead of calling getSession() directly
 * 
 * ⚠️ IMPORTANT: For critical auth checks (like auto-logout), use getSessionImmediate()
 */
export const getSessionDebounced = async (): Promise<{ session: Session | null; user: User | null }> => {
  const now = Date.now();
  
  // If called within debounce period, return cached result or wait
  if (now - lastSessionCall < DEBOUNCE_DELAY) {
    if (getSessionTimeout) {
      clearTimeout(getSessionTimeout);
    }
    
    return new Promise((resolve) => {
      getSessionTimeout = setTimeout(async () => {
        lastSessionCall = Date.now();
        const { data: { session } } = await supabase.auth.getSession();
        resolve({ session, user: session?.user ?? null });
      }, DEBOUNCE_DELAY - (now - lastSessionCall));
    });
  }
  
  lastSessionCall = now;
  const { data: { session } } = await supabase.auth.getSession();
  return { session, user: session?.user ?? null };
};

/**
 * Immediate getSession for critical auth checks (auto-logout, security checks)
 * Bypasses debouncing to ensure immediate response
 */
export const getSessionImmediate = async (): Promise<{ session: Session | null; user: User | null }> => {
  const { data: { session } } = await supabase.auth.getSession();
  return { session, user: session?.user ?? null };
};

/**
 * Get current user from session instead of calling getUser()
 * This eliminates one API call
 */
export const getCurrentUser = async (): Promise<User | null> => {
  const { user } = await getSessionDebounced();
  return user;
};

/**
 * Get current session with debouncing
 */
export const getCurrentSession = async (): Promise<Session | null> => {
  const { session } = await getSessionDebounced();
  return session;
};

/**
 * Check if user is authenticated without calling getUser()
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const { user } = await getSessionDebounced();
  return !!user;
};

/**
 * Get user ID from session without calling getUser()
 */
export const getCurrentUserId = async (): Promise<string | null> => {
  const { user } = await getSessionDebounced();
  return user?.id ?? null;
};

/**
 * Clear any pending debounced calls
 */
export const clearAuthDebounce = () => {
  if (getSessionTimeout) {
    clearTimeout(getSessionTimeout);
    getSessionTimeout = null;
  }
}; 